import React, { useEffect, useState } from 'react';
import { Flex, InputV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { Close, Search } from '@withjoy/joykit/icons';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useSearchContext } from './SearchContext';
import { SEARCH_BOX_MAX_WIDTH } from '../Filters/useSearchBoxWidth';
import { useRotatingText } from '@shared/hooks/useRotatingText';

const V2_SEARCH_QUERY_PARAM = 'q';

type SearchBoxProps = Readonly<{
  compact?: boolean;
  /** Width of the compact box from the `sm2` breakpoint up; it is fluid below. Defaults to the full width. */
  desktopWidth?: number;
}>;

export const SearchBox: React.FC<SearchBoxProps> = ({ compact, desktopWidth = SEARCH_BOX_MAX_WIDTH }) => {
  const { tq } = useTranslation('stationery');
  const [initialQ] = useManagedSearchParam(V2_SEARCH_QUERY_PARAM);
  const [term, setTerm] = useState(initialQ ?? '');
  const { setTerm: setSearchContextTerm } = useSearchContext();
  const placeholderExample = useRotatingText([tq`Beach`, tq`Floral`, tq`Green`]);
  // A narrowed box would clip "Search for Beach" mid-word, so any narrowing drops the example.
  const placeholder = compact && desktopWidth < SEARCH_BOX_MAX_WIDTH ? tq`Search` : `${tq`Search for`} ${placeholderExample}`;

  useEffect(() => {
    setSearchContextTerm(term);
  }, [term, setSearchContextTerm]);

  return (
    <InputV2
      type="text"
      placeholder={placeholder}
      autoComplete="off"
      value={term}
      onChange={event => setTerm(event.target.value)}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          setTerm('');
        }
      }}
      overrides={{
        Root: {
          props: {
            borderRadius: 24,
            // Fixed 288px normally; fluid on mobile in the compact filter-bar so the "Filters"
            // control can sit beside it without overflowing. On desktop the filter bar may narrow
            // it (`desktopWidth`) to keep the filter pills on one line.
            width: compact ? { _: '100%', sm2: desktopWidth } : 288,
            // Match the filter-pill / preview-button height (48px) in the gated filter-bar design.
            ...(compact ? { height: pxToRem(48) } : {}),
          },
        },
        ...(compact ? { Input: { props: { height: '100%', paddingY: 0 } } } : {}),
      }}
      startElement={<Search size={24} />}
      endElement={
        <Flex
          onClick={() => setTerm('')}
          role="button"
          aria-label={tq`Clear search`}
          tabIndex={term ? 0 : -1}
          {...(term
            ? {
                cursor: 'pointer',
                visibility: 'visible',
                pointerEvents: 'auto',
              }
            : {
                cursor: 'auto',
                visibility: 'hidden',
                pointerEvents: 'none',
              })}
          paddingX={1}
        >
          <Close size={24} />
        </Flex>
      }
    />
  );
};
